import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

export interface Supplement {
  supplement_id?: number;
  id?: number;
  supplement_name: string;
  description: string;
  instructions_for_taking?: string;
  image?: string | null;
  recommended_dosage?: number;
  dosage_units?: string;
  interactions_with_medications?: string;
  biomarker_ids?: number[];
  created_at?: string;
  updated_at?: string;
}

export interface SupplementRegime {
  id?: number;
  regime_id?: number;
  name: string;
  description?: string;
  supplements: Supplement[];
  member_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ListSupplementRegimesResponse {
  supplement_regimes: SupplementRegime[];
  total_count?: number;
  limit?: number;
  offset?: number;
}

export interface CreateSupplementRegimeRequest {
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
}

export interface CreateSupplementRegimeResponse {
  id: number;
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
  created_at: string;
  updated_at: string;
  status: string;
}

export interface UpdateSupplementRegimeRequest {
  regime_id: number;
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
}

export interface UpdateSupplementRegimeResponse {
  id: number;
  member_id: string;
  supplement_id: number;
  dosage: number;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
  created_at: string;
  updated_at: string;
  status: string;
}

export interface DeleteSupplementRegimeResponse {
  id: number;
  status: string;
}

export const getSupplementRegimesList = async (
  search?: string,
  sort_by?: string,
  sort_direction?: string,
  limit: number = 10,
  offset: number = 0
): Promise<ListSupplementRegimesResponse> => {
  try {
    const params: any = { limit, offset };
    if (search) params.search = search;
    if (sort_by) params.sort_by = sort_by;
    if (sort_direction) params.sort_direction = sort_direction;

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}supplement-regimes/`,
      { params }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch supplement regimes');
  }
};

export const getSupplementRegimesByMember = async (memberId: string): Promise<SupplementRegime[]> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}supplement-regimes/member/${memberId}`
    );
    return data.supplement_regimes || data || [];
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch member supplement regimes');
  }
};

export const createSupplementRegime = async (
  newSupplementRegimeRequestObject: CreateSupplementRegimeRequest
): Promise<CreateSupplementRegimeResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}supplement-regimes/`,
      newSupplementRegimeRequestObject,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to create supplement regime');
  }
};

export const updateSupplementRegime = async (
  supplementRegimeRequestObject: UpdateSupplementRegimeRequest
): Promise<UpdateSupplementRegimeResponse> => {
  try {
    const { regime_id, ...updateData } = supplementRegimeRequestObject;
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}supplement-regimes/${regime_id}/`,
      updateData
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to update supplement regime');
  }
};

export const deleteSupplementRegime = async (regime_id: number): Promise<DeleteSupplementRegimeResponse> => {
  try {
    const { data } = await axiosInstance.delete(
      `${API_V1_PREFIX}supplement-regimes/${regime_id}/`
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to delete supplement regime');
  }
};

export const getSupplementRegimeById = async (regime_id: number): Promise<SupplementRegime> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}supplement-regimes/${regime_id}/`
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || error?.message || 'Failed to fetch supplement regime');
  }
};

export interface UnassignSupplementRegimeRequest {
  regime_id: number;
  member_id: string;
}

export interface UnassignSupplementRegimeResponse {
  status: string;
}

export const unassignSupplementRegime = async (
  unassignRequest: UnassignSupplementRegimeRequest
): Promise<UnassignSupplementRegimeResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}supplement-regimes/unassign`,
      unassignRequest,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to unassign supplement regime');
  }
};